import React, { useEffect, useRef, useState } from "react";
import {
  RecBar,
  RecField,
  RecIcon,
  RecThumb,
  useKeepFocusedFieldVisible,
  useKeyboardOpen,
} from "./MaterialRecordParts";
import { optimizeMaterialImage } from "../../helpers/materialImages";
import { MaterialPhotoSheet } from "./MaterialPhotoSheet";
import { markupFromPrice, priceFromMarkup } from "../../helpers/estimateLinePricing";
import {
  costLabel,
  emptyMaterialForm,
  formFromMaterial,
  isServiceType,
  materialTypeOptions,
  usesInventory,
} from "./materialRecordModel";

function sanitizeAmount(value) {
  return String(value ?? "").replace(/[$,\s]/g, "");
}

/** Route a validation message to the field it is about. */
function fieldForError(message) {
  if (/name/i.test(message)) return "material_name";
  if (/inventory/i.test(message)) return "current_inventory";
  if (/unit (cost|rate)|cost|rate/i.test(message)) return "unit_cost";
  return null;
}

/**
 * EDIT / CREATE MODE: one component, one visual language for both. Compact
 * 44px controls, related fields share rows, sticky Cancel / Save.
 *
 * onSubmit(form) -> Promise<{ error?: string }>; the parent owns persistence.
 */
export function MaterialEditor({
  material,
  canViewPricing,
  onCancel,
  onSubmit,
  imageUrl,
  photosEnabled = false,
}) {
  const isEditing = Boolean(material);
  const [form, setForm] = useState(() =>
    isEditing ? formFromMaterial(material) : emptyMaterialForm(),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState(null);
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);

  // Photo: "keep" (unchanged) | "set" (new optimized image) | "remove"
  const [photo, setPhoto] = useState({ status: "keep", blob: null, previewUrl: "" });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const cameraInput = useRef(null);
  const libraryInput = useRef(null);

  useEffect(() => () => {
    if (photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
  }, [photo.previewUrl]);

  const hadPhoto = Boolean(material?.image_path);
  const shownImage =
    photo.status === "set" ? photo.previewUrl : photo.status === "remove" ? "" : imageUrl || "";
  const hasPhoto = Boolean(shownImage) || (photo.status === "keep" && hadPhoto);

  async function handleFile(event) {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow picking the same file again
    if (!file) return;
    setSheetOpen(false);
    setPhotoError("");
    setProcessing(true);
    try {
      const { blob } = await optimizeMaterialImage(file);
      setPhoto({ status: "set", blob, previewUrl: URL.createObjectURL(blob) });
    } catch (error) {
      setPhotoError(error?.message || "Unable to use that photo.");
    } finally {
      setProcessing(false);
    }
  }

  function removePhoto() {
    setSheetOpen(false);
    setPhotoError("");
    setPhoto({ status: "remove", blob: null, previewUrl: "" });
  }

  const service = isServiceType(form.material_type);
  const rate = costLabel(form.material_type);

  function update(patch) {
    setForm((current) => ({ ...current, ...patch }));
    if (errorField) {
      setError("");
      setErrorField(null);
    }
  }

  async function handleSave() {
    if (saving) return;
    setError("");
    setErrorField(null);
    setSaving(true);
    try {
      let imageChange = null;
      if (photosEnabled) {
        if (photo.status === "set") imageChange = { type: "set", blob: photo.blob };
        else if (photo.status === "remove" && hadPhoto) imageChange = { type: "remove" };
      }
      const result = await onSubmit(
        {
          ...form,
          unit_cost: sanitizeAmount(form.unit_cost),
          sell_price: sanitizeAmount(form.sell_price),
          current_inventory: sanitizeAmount(form.current_inventory),
        },
        imageChange,
      );
      if (result?.error) {
        const message = String(result.error).replace(/unit cost/i, rate.toLowerCase());
        setError(message);
        setErrorField(fieldForError(message));
      }
    } finally {
      setSaving(false);
    }
  }

  const fieldError = (name) => (errorField === name ? error : undefined);
  const bannerError = error && !errorField ? error : "";

  return (
    <div className="screen nx-screen rec-page rec-page--edit" data-testid="material-editor">
      <RecBar
        title={isEditing ? "Edit Material" : "Add Material"}
        leading="close"
        onBack={onCancel}
      />

      <div className="rec-body">
        <section className="rec-card rec-head">
          {photosEnabled ? (
            <div className="rec-photo">
              <button
                type="button"
                className="rec-photo__btn"
                onClick={() => setSheetOpen(true)}
                aria-label={hasPhoto ? "Change photo" : "Add photo"}
                disabled={processing}
              >
                <RecThumb
                  name={form.material_name}
                  type={form.material_type}
                  imageUrl={shownImage}
                  size="md"
                />
                <span className="rec-photo__badge" aria-hidden="true">
                  <RecIcon name="camera" />
                </span>
                {processing ? <span className="rec-photo__busy" role="status" aria-label="Preparing photo" /> : null}
              </button>
              <input
                ref={cameraInput}
                type="file"
                accept="image/*"
                capture="environment"
                className="rec-photo__input"
                data-testid="photo-camera"
                tabIndex={-1}
                onChange={handleFile}
              />
              <input
                ref={libraryInput}
                type="file"
                accept="image/*"
                className="rec-photo__input"
                data-testid="photo-library"
                tabIndex={-1}
                onChange={handleFile}
              />
            </div>
          ) : (
            <RecThumb name={form.material_name} type={form.material_type} imageUrl={shownImage} size="md" />
          )}
          <div className="rec-head__fields">
            <RecField
              label="Material Name"
              htmlFor="rec-name"
              required
              error={fieldError("material_name")}
            >
              <input
                id="rec-name"
                className="rec-input"
                value={form.material_name}
                onChange={(e) => update({ material_name: e.target.value })}
                placeholder="e.g. Ceramic Tile"
                autoComplete="off"
              />
            </RecField>
            <RecField label="Type" htmlFor="rec-type">
              <span className="rec-select">
                <select
                  id="rec-type"
                  className="rec-input"
                  value={form.material_type}
                  onChange={(e) => update({ material_type: e.target.value })}
                >
                  {materialTypeOptions(form.material_type).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </span>
            </RecField>
          </div>
        </section>

        {photoError ? (
          <div className="rec-banner" role="alert">
            {photoError}
          </div>
        ) : null}

        <section className="rec-card rec-grid">
          <RecField label="Unit of Measure" htmlFor="rec-uom" icon="cube">
            <input
              id="rec-uom"
              className="rec-input"
              value={form.unit_of_measure}
              onChange={(e) => update({ unit_of_measure: e.target.value })}
              placeholder="sq ft, ea, hr"
              autoComplete="off"
            />
          </RecField>

          {canViewPricing ? (
            <RecField
              label={service ? rate : "Our cost"}
              htmlFor="rec-cost"
              icon="tag"
              error={fieldError("unit_cost")}
            >
              <span className="rec-money">
                <span className="rec-money__sym" aria-hidden="true">$</span>
                <input
                  id="rec-cost"
                  className="rec-input rec-input--money"
                  value={form.unit_cost}
                  onChange={(e) => update({ unit_cost: e.target.value })}
                  placeholder="0.00"
                  inputMode="decimal"
                  autoComplete="off"
                />
              </span>
            </RecField>
          ) : null}

          {canViewPricing && !service ? (
            <>
              <RecField label="Client price" htmlFor="rec-sell" icon="tag">
                <span className="rec-money">
                  <span className="rec-money__sym" aria-hidden="true">$</span>
                  <input
                    id="rec-sell"
                    className="rec-input rec-input--money"
                    value={form.sell_price}
                    onChange={(e) => update({ sell_price: e.target.value })}
                    placeholder="0.00"
                    inputMode="decimal"
                    autoComplete="off"
                  />
                </span>
              </RecField>
              <RecField
                label="Markup %"
                htmlFor="rec-markup"
                hint={form.unit_cost && form.sell_price
                  ? `Implied markup ${markupFromPrice(sanitizeAmount(form.unit_cost), sanitizeAmount(form.sell_price)) ?? "—"}%`
                  : "Sets the client price from our cost"}
              >
                <input
                  id="rec-markup"
                  className="rec-input"
                  inputMode="decimal"
                  placeholder="e.g. 40"
                  onChange={(e) => {
                    const pct = e.target.value;
                    const next = priceFromMarkup(sanitizeAmount(form.unit_cost), pct);
                    update({ sell_price: pct.trim() === "" ? form.sell_price : String(next) });
                  }}
                />
              </RecField>
            </>
          ) : null}

          <RecField label="SKU" htmlFor="rec-sku" icon="barcode">
            <input
              id="rec-sku"
              className="rec-input"
              value={form.sku}
              onChange={(e) => update({ sku: e.target.value })}
              placeholder="SKU"
              autoComplete="off"
            />
          </RecField>

          {canViewPricing && usesInventory(form.material_type) ? (
            <RecField
              label="Current Inventory"
              htmlFor="rec-stock"
              icon="box"
              error={fieldError("current_inventory")}
            >
              <input
                id="rec-stock"
                className="rec-input"
                value={form.current_inventory}
                onChange={(e) => update({ current_inventory: e.target.value })}
                placeholder="0"
                inputMode="decimal"
                autoComplete="off"
              />
            </RecField>
          ) : null}
        </section>

        <section className="rec-card">
          <RecField label="Description" htmlFor="rec-desc" icon="doc">
            <textarea
              id="rec-desc"
              className="rec-input rec-input--area"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              placeholder={service ? "What this service covers" : "Size, finish, notes"}
              rows={3}
            />
          </RecField>
        </section>

        <label className="rec-card rec-check">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => update({ active: e.target.checked })}
          />
          <span className="rec-check__box" aria-hidden="true">
            <RecIcon name="check" />
          </span>
          <span className="rec-check__text">
            <b>Active</b>
            <small>Available for use in CrewPilot.</small>
          </span>
        </label>

        {bannerError ? (
          <div className="rec-banner" role="alert">
            {bannerError}
          </div>
        ) : null}
      </div>

      <div className="rec-actions rec-actions--split">
        <button type="button" className="rec-btn rec-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {sheetOpen ? (
        <MaterialPhotoSheet
          hasPhoto={hasPhoto}
          onTake={() => {
            setSheetOpen(false);
            cameraInput.current?.click();
          }}
          onChoose={() => {
            setSheetOpen(false);
            libraryInput.current?.click();
          }}
          onRemove={removePhoto}
          onClose={() => setSheetOpen(false)}
        />
      ) : null}
    </div>
  );
}
